import React, { useEffect, useState } from "react";
import {
    Navigate,
    Outlet,
    Route,
    Routes,
    useSearchParams,
} from "react-router-dom";
import { REFERRAL_MODAL_KEY } from "@constants";
import { ReferralModal } from "@components/settings/Referrals/ReferralModal";
import { PREMIUM_FEATURES } from "../../common/flatRateBilling";
import { ActionsPage } from "../actions/ActionsPage";
import { ActionsProvider } from "../actions/actionsContext";
import { ActionsTabs } from "../actions/ActionsTabs";
import { AccountingPage } from "../accounting/AccountingPage";
import { BalanceSheetAccessPage } from "../accounting/balanceSheet/BalanceSheetAccessPage";
import { ChartOfAccountsPage } from "../accounting/chartOfAccounts/ChartOfAccounts";
import { GeneralLedgerAccessPage } from "../accounting/generalLedger/GeneralLedgerAccessPage";
import { ProfitLossPreviewPage } from "../accounting/profitLoss/ProfitLossPreviewPage";
import { AccountingTabProvider } from "../accounting/useAccountingTab";
import { TrialBalanceAccessPage } from "../accounting/trialBalance/TrialBalanceAccessPage";
import { AccountsPage } from "../accounts/AccountsPage";
import { AddAccountsRoute } from "../accounts/AddAccounts/AddAccountsRoute";
import { DocsToDocumentsRedirect } from "../documents/DocsToDocumentsRedirect";
import { FinancialDocumentsPage } from "../documents/FinancialDocumentsPage";
import { FinancialDocumentUploadProvider } from "../documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { FeatureAccess } from "../general/FeatureAccess/FeatureAccess";
import { InvoicesPage } from "../invoices/InvoicesPage";
import { NewEntity } from "../onboarding/flows/NewEntity";
import { CashFlowPage } from "../reports/CashFlowPage";
import { CategoriesPage } from "../settings/Categories/CategoriesPage";
import { ClassesPage } from "../settings/Classes/ClassesPage";
import { RulesPage } from "../settings/Rules/RulesPage";
import { SettingsRoute } from "../settings/SettingsRoute";
import { TransactionsPageContainer } from "../transactions/TransactionsPageContainer";
import { MainInterface } from "../mainInterface/MainInterface";
import { useAccountingBreadcrumbs } from "./useAccountingBreadcrumbs";
import { useRenderCommonRoutes } from "./useRenderCommonRoutes";

export const MainRoutes: React.FC = () => {
    const accountingBreadcrumbs = useAccountingBreadcrumbs();
    const commonRoutes = useRenderCommonRoutes();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showReferralModal, setShowReferralModal] = useState(false);

    useEffect(() => {
        const referParam = searchParams.get(REFERRAL_MODAL_KEY);
        if (referParam === "true") {
            setShowReferralModal(true);
            searchParams.delete(REFERRAL_MODAL_KEY);
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    return (
        <>
            <Routes>
                <Route
                    path="/"
                    element={
                        <ActionsProvider>
                            <MainInterface
                                breadcrumbs={[{ label: "Actions" }]}
                                subNavigation={<ActionsTabs />}
                            >
                                <ActionsPage />
                            </MainInterface>
                        </ActionsProvider>
                    }
                />

                <Route
                    path="/actions/:category?"
                    element={
                        <ActionsProvider>
                            <MainInterface
                                breadcrumbs={[{ label: "Actions" }]}
                                subNavigation={<ActionsTabs />}
                            >
                                <ActionsPage />
                            </MainInterface>
                        </ActionsProvider>
                    }
                />

                <Route
                    path="/invoicing"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Invoicing" }]}>
                            <InvoicesPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/accounts"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Accounts" }]}>
                            <AccountsPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/transactions/:entityId?"
                    element={
                        <MainInterface
                            breadcrumbs={[{ label: "Transactions" }]}
                        >
                            <TransactionsPageContainer />
                        </MainInterface>
                    }
                />

                <Route
                    path="/rules"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Rules" }]}>
                            <RulesPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/categories"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Categories" }]}>
                            <CategoriesPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/classes"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Classes" }]}>
                            <ClassesPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/cashflow"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Cash Flow" }]}>
                            <CashFlowPage />
                        </MainInterface>
                    }
                />

                <Route
                    path="/documents/:documentId?/:view?"
                    element={
                        <MainInterface breadcrumbs={[{ label: "Documents" }]}>
                            <FinancialDocumentUploadProvider>
                                <FinancialDocumentsPage />
                            </FinancialDocumentUploadProvider>
                        </MainInterface>
                    }
                />

                <Route path="/docs/*" element={<DocsToDocumentsRedirect />} />

                <Route
                    path="/accounting"
                    element={
                        <MainInterface breadcrumbs={accountingBreadcrumbs}>
                            <AccountingTabProvider>
                                <Outlet />
                            </AccountingTabProvider>
                        </MainInterface>
                    }
                >
                    <Route index element={<AccountingPage />} />
                    <Route
                        path="profit-loss"
                        element={<ProfitLossPreviewPage />}
                    />
                    <Route
                        path="balance-sheet"
                        element={<BalanceSheetAccessPage />}
                    />
                    <Route
                        path="general-ledger"
                        element={<GeneralLedgerAccessPage />}
                    />
                    <Route
                        path="chart-of-accounts"
                        element={<ChartOfAccountsPage />}
                    />
                    <Route
                        path="trial-balance"
                        element={<TrialBalanceAccessPage />}
                    />
                </Route>

                <Route
                    path="/new-entity"
                    element={
                        <FeatureAccess
                            feature={PREMIUM_FEATURES.UNLIMITED_ENTITIES}
                            renderAllowed={() => <NewEntity />}
                            renderBlocked={() => (
                                <Navigate to="/settings/plans" replace />
                            )}
                        />
                    }
                />

                <Route path="/settings/*" element={<SettingsRoute />} />
                <Route path={`/add-account`} element={<AddAccountsRoute />} />

                {commonRoutes}

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            <ReferralModal
                show={showReferralModal}
                onHide={() => setShowReferralModal(false)}
            />
        </>
    );
};
